import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ObraOTsTabProps {
  obraId: string;
}

interface OrdenTrabajo {
  id: string;
  numero_ot: string;
  titulo: string;
  estado: string;
  prioridad: string;
  fecha_limite: string | null;
  tecnico_asignado: { nombre: string } | null;
}

interface ParteTrabajo {
  id: string;
  duracion_total: string | null;
}

export function ObraOTsTab({ obraId }: ObraOTsTabProps) {
  const [ordenes, setOrdenes] = useState<OrdenTrabajo[]>([]);
  const [horasPartes, setHorasPartes] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load OTs linked to this obra
        const { data: otsData, error: otsError } = await supabase
          .from('ordenes_trabajo')
          .select(`
            id,
            numero_ot,
            titulo,
            estado,
            prioridad,
            fecha_limite,
            tecnico_asignado:perfiles!ordenes_trabajo_tecnico_asignado_id_fkey(nombre)
          `)
          .eq('obra_id', obraId)
          .order('created_at', { ascending: false });

        if (otsError) throw otsError;
        setOrdenes(otsData || []);

        // Calculate total hours from partes
        if (otsData && otsData.length > 0) {
          const otIds = otsData.map(ot => ot.id);
          const { data: partesData } = await supabase
            .from('partes_trabajo')
            .select('duracion_total')
            .in('orden_trabajo_id', otIds);

          if (partesData) {
            const totalHours = partesData.reduce((sum, parte) => {
              if (parte.duracion_total && typeof parte.duracion_total === 'string') {
                // Parse interval format like "01:30:00"
                const parts = (parte.duracion_total as string).split(':');
                const hours = parseInt(parts[0] || '0') + parseInt(parts[1] || '0') / 60;
                return sum + hours;
              }
              return sum;
            }, 0);
            setHorasPartes(totalHours);
          }
        }
      } catch (error) {
        console.error('Error loading OTs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [obraId]);

  const getEstadoBadge = (estado: string) => {
    const colors: Record<string, string> = {
      'creada': 'bg-gray-100 text-gray-800',
      'asignada': 'bg-blue-100 text-blue-800',
      'en_progreso': 'bg-yellow-100 text-yellow-800',
      'finalizada': 'bg-green-100 text-green-800',
      'cancelada': 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      'creada': 'Creada',
      'asignada': 'Asignada',
      'en_progreso': 'En Progreso',
      'finalizada': 'Finalizada',
      'cancelada': 'Cancelada',
    };
    return <Badge className={colors[estado]}>{labels[estado] || estado}</Badge>;
  };

  const getPrioridadBadge = (prioridad: string) => {
    const colors: Record<string, string> = {
      'baja': 'bg-blue-100 text-blue-800',
      'media': 'bg-yellow-100 text-yellow-800',
      'alta': 'bg-orange-100 text-orange-800',
      'urgente': 'bg-red-100 text-red-800',
    };
    return <Badge variant="outline" className={colors[prioridad]}>{prioridad}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              OTs Vinculadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ordenes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Horas en Partes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{horasPartes.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">De partes de trabajo asociados</p>
          </CardContent>
        </Card>
      </div>

      {/* OTs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Órdenes de Trabajo Vinculadas</CardTitle>
          <CardDescription>
            OTs asociadas a esta obra. Las horas de sus partes de trabajo se suman automáticamente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ordenes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead>Técnico</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordenes.map((orden) => (
                  <TableRow key={orden.id}>
                    <TableCell className="font-mono">{orden.numero_ot}</TableCell>
                    <TableCell className="font-medium">{orden.titulo}</TableCell>
                    <TableCell>{getEstadoBadge(orden.estado)}</TableCell>
                    <TableCell>{getPrioridadBadge(orden.prioridad)}</TableCell>
                    <TableCell>
                      {orden.tecnico_asignado?.nombre || (
                        <span className="text-muted-foreground">Sin asignar</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {orden.fecha_limite 
                        ? format(new Date(orden.fecha_limite), 'dd/MM/yyyy', { locale: es })
                        : '-'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Sin OTs vinculadas</h3>
              <p className="text-muted-foreground">
                Puedes vincular órdenes de trabajo a esta obra desde el formulario de OT
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
